"use client"

import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { 
  Trophy, 
  TrendingUp, 
  Users, 
  Calendar,
  Star,
  Award,
  Target,
  BookOpen
} from "lucide-react"

const achievements = [
  {
    icon: <Trophy className="h-8 w-8 text-yellow-500" />,
    title: "2024年上半年通过率",
    value: "95.2%",
    description: "使用我们平台学习的学员考试通过率",
    trend: "+5.2%",
    color: "yellow"
  },
  {
    icon: <Users className="h-8 w-8 text-blue-500" />,
    title: "累计服务学员",
    value: "80,000+",
    description: "自平台上线以来累计服务的学员总数",
    trend: "+15,000",
    color: "blue"
  },
  {
    icon: <BookOpen className="h-8 w-8 text-green-500" />,
    title: "题库规模",
    value: "120万+",
    description: "涵盖所有高级科目的精选题目",
    trend: "+25万",
    color: "green"
  },
  {
    icon: <Target className="h-8 w-8 text-purple-500" />,
    title: "平均提分幅度",
    value: "28分",
    description: "学员使用平台前后的平均成绩提升",
    trend: "+3分",
    color: "purple"
  }
]

const successStories = [
  {
    name: "张工程师",
    role: "系统架构设计师",
    company: "阿里巴巴",
    score: "75分",
    studyTime: "4个月",
    avatar: "👨‍💻",
    quote: "AI推荐的学习路径非常精准，帮我在有限时间内高效备考，一次性通过考试。",
    highlights: ["AI智能推荐", "高效备考", "一次通过"],
    improvement: "从模拟考试45分提升到75分"
  },
  {
    name: "李项目经理",
    role: "信息系统项目管理师",
    company: "腾讯",
    score: "78分",
    studyTime: "3个月",
    avatar: "👩‍💼",
    quote: "智能错题分析功能太棒了，让我快速找到薄弱环节，针对性提升效果显著。",
    highlights: ["错题智能分析", "薄弱环节突破", "针对性提升"],
    improvement: "错题率从65%降低到15%"
  },
  {
    name: "王同学",
    role: "网络规划设计师",
    company: "华为",
    score: "82分",
    studyTime: "5个月",
    avatar: "👨‍🎓",
    quote: "学习社区的讨论很活跃，和其他学员交流让我收获很多，学习不再孤单。",
    highlights: ["社区互动", "经验分享", "共同进步"],
    improvement: "从零基础到高分通过"
  }
]

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring" as const,
      stiffness: 100,
      damping: 10,
    },
  },
}

export const LearningAchievementsSection = () => {
  return (
    <section className="w-full py-20 md:py-28 bg-gradient-to-br from-slate-900 to-gray-900 text-white">
      <div className="container mx-auto px-4 md:px-6">
        {/* 平台成就数据 */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            数据见证学习成果
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            真实的数据，见证每一位学员的成长与成功
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {achievements.map((achievement, index) => (
            <motion.div
              key={achievement.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={cardVariants}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-white/10 border-white/20 text-white h-full">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {achievement.icon}
                  </div>
                  <div className="text-3xl font-bold mb-2">{achievement.value}</div>
                  <div className="text-sm text-slate-300 mb-3">{achievement.title}</div>
                  <div className="text-xs text-slate-400 mb-3">{achievement.description}</div>
                  <Badge 
                    variant="secondary" 
                    className={`bg-${achievement.color}-500/20 text-${achievement.color}-300 border-${achievement.color}-500/30`}
                  >
                    {achievement.trend}
                  </Badge>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* 学员成功案例 */}
        <div className="text-center mb-12">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            学员成功案例
          </h3>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            听听他们的学习故事，也许下一个成功的就是你
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {successStories.map((story, index) => (
            <motion.div
              key={story.name}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={cardVariants}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-white/10 border-white/20 text-white h-full">
                <CardContent className="p-6">
                  {/* 用户信息 */}
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-3xl">{story.avatar}</span>
                    <div>
                      <h4 className="font-semibold text-lg">{story.name}</h4>
                      <p className="text-sm text-slate-300">{story.role}</p>
                      <p className="text-xs text-slate-400">{story.company}</p>
                    </div>
                  </div>

                  {/* 成绩信息 */}
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                      <div className="text-center">
                        <div className="text-xl font-bold text-green-400">{story.score}</div>
                        <div className="text-xs text-slate-400">考试成绩</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl font-bold text-blue-400">{story.studyTime}</div>
                        <div className="text-xs text-slate-400">学习周期</div>
                      </div>
                    </div>
                    <div className="p-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg">
                      <div className="text-xs text-orange-300 text-center">
                        📈 {story.improvement}
                      </div>
                    </div>
                  </div>

                  {/* 评价内容 */}
                  <div className="mb-4">
                    <div className="flex mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-slate-300 italic">&quot;{story.quote}&quot;</p>
                  </div>

                  {/* 学习亮点 */}
                  <div className="flex flex-wrap gap-2">
                    {story.highlights.map((highlight, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary" 
                        className="bg-orange-500/20 text-orange-300 border-orange-500/30 text-xs"
                      >
                        {highlight}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* 行动号召 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl p-8 max-w-4xl mx-auto">
            <Award className="h-12 w-12 text-white mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4">加入成功学员的行列</h3>
            <p className="text-orange-100 mb-6 max-w-2xl mx-auto">
              数万学员已经通过我们的平台成功获得软考高级认证，现在轮到你了！
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2">
                <Calendar className="h-4 w-4 mr-2" />
                2024年下半年考试报名中
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2">
                <TrendingUp className="h-4 w-4 mr-2" />
                现在开始学习正当时
              </Badge>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
